import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  username: string;
}

// Simple utility for conditional classNames
const cn = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ');

function Layout({ children, onLogout, username }: LayoutProps): React.ReactElement {
  // Persistent Knowledge Base submenu state
  const [isKBMenuOpen, setIsKBMenuOpen] = useState<boolean>(() => {
    const v = localStorage.getItem('kbMenuOpen');
    return v ? v === '1' : true;
  });
  useEffect(() => {
    localStorage.setItem('kbMenuOpen', isKBMenuOpen ? '1' : '0');
  }, [isKBMenuOpen]);

  // Desktop: collapse/expand sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    const v = localStorage.getItem('sidebarCollapsed');
    return v ? v === '1' : false;
  });
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isSidebarCollapsed ? '1' : '0');
  }, [isSidebarCollapsed]);

  // Mobile: drawer open/close
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-neutral-950 dark:text-neutral-100">
      {/* Skip link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 z-[100] rounded bg-blue-600 px-3 py-2 text-white shadow"
      >
        Skip to content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-neutral-900/80 backdrop-blur border-b border-gray-200 dark:border-neutral-800 shadow-sm">
        <div className="mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(true)}
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:hidden"
              aria-label="Open sidebar navigation"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="font-bold text-lg tracking-tight text-blue-700 dark:text-blue-400">
              Variable Soft
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div
              className="hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white font-semibold"
              aria-hidden="true"
              title={username}
            >
              {username?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <span className="hidden sm:inline text-sm text-gray-700 dark:text-neutral-300">{username}</span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-semibold text-gray-800 dark:text-neutral-100 hover:bg-gray-50 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <svg className="w-4 h-4 -ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Mobile overlay + drawer */}
        {isMobileNavOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsMobileNavOpen(false)}
              aria-hidden="true"
            />
            <aside className="absolute left-0 top-0 h-full w-72 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 shadow-xl transform transition-transform duration-200 ease-out">
              <SidebarContent
                isCollapsed={false}
                isKBMenuOpen={isKBMenuOpen}
                setIsKBMenuOpen={setIsKBMenuOpen}
                onCollapseToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                showCollapseToggle={false}
                onCloseMobile={() => setIsMobileNavOpen(false)}
              />
            </aside>
          </div>
        )}

        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden lg:flex lg:flex-col border-r border-gray-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/60 backdrop-blur",
            "transition-all duration-200 ease-out",
            isSidebarCollapsed ? "w-20" : "w-72"
          )}
        >
          <SidebarContent
            isCollapsed={isSidebarCollapsed}
            isKBMenuOpen={isKBMenuOpen}
            setIsKBMenuOpen={setIsKBMenuOpen}
            onCollapseToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            showCollapseToggle
          />
        </aside>

        {/* Main content */}
        <main id="main-content" className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}

export default Layout;

/* ----------------- SidebarContent (internal) ----------------- */

interface SidebarContentProps {
  isCollapsed: boolean;
  isKBMenuOpen: boolean;
  setIsKBMenuOpen: (open: boolean) => void;
  onCollapseToggle: () => void;
  showCollapseToggle?: boolean;
  onCloseMobile?: () => void;
}

function SidebarContent({
  isCollapsed,
  isKBMenuOpen,
  setIsKBMenuOpen,
  onCollapseToggle,
  showCollapseToggle = true,
  onCloseMobile,
}: SidebarContentProps): React.ReactElement {
  const linkBase =
    "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 ring-offset-2 ring-offset-white dark:ring-offset-neutral-900 focus-visible:ring-blue-500";
  const linkInactive = "text-gray-700 hover:bg-blue-50 hover:text-blue-700 dark:text-neutral-200 dark:hover:bg-neutral-800/80";
  const linkActive =
    "text-blue-700 bg-blue-50 border-l-4 border-blue-600 pl-2 hover:text-blue-700 dark:text-white dark:bg-blue-600/90 dark:border-blue-500";

  return (
    <nav className="flex h-full flex-col px-3 py-6" aria-label="Sidebar">
      {/* Top collapse/expand toggle */}
      {showCollapseToggle && (
        <div className={cn("mb-3", isCollapsed ? "flex items-center justify-center" : "px-1")}>
          <button
            type="button"
            onClick={onCollapseToggle}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 dark:border-neutral-800 text-sm font-medium text-gray-700 dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
              isCollapsed ? "p-2" : "px-3 py-2 w-full"
            )}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg
              className={cn("h-4 w-4 transition-transform", isCollapsed ? "rotate-180" : "")}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {!isCollapsed && <span>{isCollapsed ? "Expand" : "Collapse"}</span>}
          </button>
        </div>
      )}

      {/* Section: Knowledge Base */}
      <div className="mb-2">
        <button
          type="button"
          onClick={() => setIsKBMenuOpen(!isKBMenuOpen)}
          className={cn(
            "w-full flex items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-100 dark:text-neutral-100 dark:hover:bg-neutral-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
            isCollapsed && "justify-center"
          )}
          aria-expanded={isKBMenuOpen}
          aria-controls="kb-submenu"
          title="Knowledge Base"
        >
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
              Knowledge Base
            </span>
          </div>
          {!isCollapsed && (
            <svg
              className={cn("w-4 h-4 transition-transform", isKBMenuOpen ? "rotate-180" : "rotate-0")}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {!isCollapsed && isKBMenuOpen && (
          <div id="kb-submenu" className="mt-1 space-y-1 pl-3">
            <NavLink
              to="/kb-upload"
              onClick={onCloseMobile}
              aria-label="Upload KB"
              title="Upload KB"
              className={({ isActive }) =>
                cn(linkBase, "pl-7", isActive ? linkActive : linkInactive)
              }
            >
              <span className="absolute left-4 h-1.5 w-1.5 rounded-full bg-gray-400 group-hover:bg-blue-600" />
              Upload KB
            </NavLink>

            <NavLink
              to="/kb-list"
              onClick={onCloseMobile}
              aria-label="List of KBs"
              title="List of KBs"
              className={({ isActive }) =>
                cn(linkBase, "pl-7", isActive ? linkActive : linkInactive)
              }
            >
              <span className="absolute left-4 h-1.5 w-1.5 rounded-full bg-gray-400 group-hover:bg-blue-600" />
              List of KBs
            </NavLink>
          </div>
        )}
      </div>

      {/* Section: Standalone links */}
      <div className="mt-3 space-y-1">
        <NavLink
          to="/playground"
          onClick={onCloseMobile}
          aria-label="Playground"
          title="Playground"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Playground
          </span>
        </NavLink>

        <NavLink
          to="/conversations"
          onClick={onCloseMobile}
          aria-label="Conversations"
          title="Conversations"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h10M7 16h6M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Conversations
          </span>
        </NavLink>

        <NavLink
          to="/querylog"
          onClick={onCloseMobile}
          aria-label="Query Log"
          title="Query Log"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Query Log
          </span>
        </NavLink>

        <NavLink
          to="/prompt-mgr"
          onClick={onCloseMobile}
          aria-label="Prompt"
          title="Prompt"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Prompt
          </span>
        </NavLink>

        <NavLink
          to="/fallback-mgr"
          onClick={onCloseMobile}
          aria-label="Fallback Messages"
          title="Fallback Messages"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Fallback Messages
          </span>
        </NavLink>

        {/* Company Questions */}
        <NavLink
          to="/company-questions"
          onClick={onCloseMobile}
          aria-label="Company Questions"
          title="Company Questions"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Company Questions
          </span>
        </NavLink>

        {/* ✅ New: Settings (API key management lives here) */}
        <NavLink
          to="/settings"
          onClick={onCloseMobile}
          aria-label="Settings"
          title="Settings"
          className={({ isActive }) =>
            cn(
              linkBase,
              "border-l-4 pl-2",
              isActive ? linkActive : linkInactive
            )
          }
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.52-.878 3.31.912 2.432 2.432a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.878 1.52-.912 3.31-2.432 2.432a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.52.878-3.31-.912-2.432-2.432a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35.68-.165 1.238-.723 1.403-1.403.426-1.756 2.924-1.756 3.35 0 .165.68.723 1.238 1.403 1.403z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span className={cn("transition-opacity", isCollapsed && "opacity-0 pointer-events-none w-0")}>
            Settings
          </span>
        </NavLink>
      </div>

      {/* Bottom collapse toggle (kept) */}
      {showCollapseToggle && (
        <div className="mt-auto pt-4">
          <button
            type="button"
            onClick={onCollapseToggle}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 dark:border-neutral-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg
              className={cn("h-4 w-4 transition-transform", isCollapsed ? "rotate-180" : "")}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {!isCollapsed && <span>Collapse</span>}
          </button>
        </div>
      )}
    </nav>
  );
}
